import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { type ReactElement, useEffect, useState } from 'react'
import { Form, Submit } from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { Confirm } from '~/components/Global/Interactions/Confirm/Confirm'
import {
  lineKey,
  type ShortlistRankingValues,
  shortlistRankingSchema
} from '../forms/shortlistRanking'
import { useIsNarrowScreen } from '../hooks/useIsNarrowScreen'
import { useSubmitShortlist } from '../mutations/submitShortlist'
import type { PublicShortlistApplicant } from '../types'
import { layout, list, listTitle } from './PublicShortlist.css'
import { ShortlistFileViewer } from './ShortlistFileViewer'
import {
  SHORTLIST_LINE_DRAG_TYPE,
  ShortlistRankingRow
} from './ShortlistRankingRow'

const moveLine = (
  lineIds: number[],
  index: number,
  offset: -1 | 1
): number[] => {
  const moved = [...lineIds]
  const [line] = moved.splice(index, 1)
  if (line === undefined) return lineIds
  moved.splice(index + offset, 0, line)
  return moved
}

const dropLine = (
  lineIds: number[],
  movedId: number,
  targetId: number,
  edge: 'top' | 'bottom'
): number[] => {
  const remaining = lineIds.filter((id) => id !== movedId)
  const targetIndex = remaining.indexOf(targetId)
  remaining.splice(edge === 'top' ? targetIndex : targetIndex + 1, 0, movedId)
  return remaining
}

const moveAboveRejected = (
  lineIds: number[],
  rejectedId: number,
  answers: ShortlistRankingValues['answers']
): number[] => {
  const remaining = lineIds.filter((id) => id !== rejectedId)
  const firstRejectedIndex = remaining.findIndex(
    (id) => answers[lineKey(id)] === 'no'
  )
  remaining.splice(
    firstRejectedIndex === -1 ? remaining.length : firstRejectedIndex,
    0,
    rejectedId
  )
  return remaining
}

type ShortlistRankingProps = {
  applicants: PublicShortlistApplicant[]
  instance: string
  token: string
}

export const ShortlistRanking = ({
  applicants,
  instance,
  token
}: ShortlistRankingProps): ReactElement => {
  const [lineIds, setLineIds] = useState(() =>
    applicants.map((applicant) => applicant.id)
  )
  const [pendingValues, setPendingValues] =
    useState<ShortlistRankingValues | null>(null)
  const [selectedId, setSelectedId] = useState(applicants[0]?.id)
  const isNarrowScreen = useIsNarrowScreen()
  const submitShortlist = useSubmitShortlist(instance, token)

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.type === SHORTLIST_LINE_DRAG_TYPE,
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0]
          const movedId = source.data.lineId
          const targetId = target?.data.lineId
          const edge = target?.data.edge
          if (
            typeof movedId !== 'number' ||
            typeof targetId !== 'number' ||
            (edge !== 'top' && edge !== 'bottom')
          ) {
            return
          }
          setLineIds((current) => dropLine(current, movedId, targetId, edge))
        }
      }),
    []
  )

  const applicantsById = new Map(
    applicants.map((applicant) => [applicant.id, applicant])
  )
  const selectedApplicant =
    selectedId !== undefined ? applicantsById.get(selectedId) : undefined
  const selectedIndex =
    selectedId !== undefined ? lineIds.indexOf(selectedId) : -1
  const previousId = selectedIndex > 0 ? lineIds[selectedIndex - 1] : undefined
  const nextId = selectedIndex >= 0 ? lineIds[selectedIndex + 1] : undefined
  const viewerNavigation = {
    position: selectedIndex + 1,
    total: lineIds.length,
    onPrevious:
      previousId !== undefined ? () => setSelectedId(previousId) : undefined,
    onNext: nextId !== undefined ? () => setSelectedId(nextId) : undefined
  }

  const confirmSubmission = (): void => {
    if (pendingValues === null) return
    submitShortlist.mutate(
      lineIds.map((id) => ({
        id,
        accepted: pendingValues.answers[lineKey(id)] === 'yes',
        comment: pendingValues.comments[lineKey(id)]?.trim() || null
      }))
    )
    setPendingValues(null)
  }

  return (
    <>
      <Form<ShortlistRankingValues>
        schema={shortlistRankingSchema}
        defaultValues={{
          answers: Object.fromEntries(
            applicants.map((applicant) => [lineKey(applicant.id), ''])
          ),
          comments: Object.fromEntries(
            applicants.map((applicant) => [lineKey(applicant.id), ''])
          )
        }}
        onSubmit={setPendingValues}
      >
        <div className={layout}>
          {!isNarrowScreen && selectedApplicant ? (
            <ShortlistFileViewer
              applicant={selectedApplicant}
              {...viewerNavigation}
            />
          ) : null}
          <div>
            <h2 className={listTitle}>Votre classement</h2>
            <div className={list}>
              {lineIds.map((id, index) => {
                const applicant = applicantsById.get(id)
                return applicant ? (
                  <ShortlistRankingRow
                    key={id}
                    applicant={applicant}
                    position={index + 1}
                    isSelected={id === selectedId}
                    onSelect={() => setSelectedId(id)}
                    preview={
                      isNarrowScreen && id === selectedId ? (
                        <ShortlistFileViewer
                          applicant={applicant}
                          {...viewerNavigation}
                        />
                      ) : undefined
                    }
                    onMoveUp={
                      index > 0
                        ? () =>
                            setLineIds((current) =>
                              moveLine(current, index, -1)
                            )
                        : undefined
                    }
                    onMoveDown={
                      index < lineIds.length - 1
                        ? () =>
                            setLineIds((current) => moveLine(current, index, 1))
                        : undefined
                    }
                    onReject={(answers) =>
                      setLineIds((current) =>
                        moveAboveRejected(current, id, answers)
                      )
                    }
                  />
                ) : null
              })}
            </div>
            {submitShortlist.isError ? (
              <Alert variation={AlertVariation.Danger} onDismiss={false}>
                Vos retours n'ont pas pu être envoyés. Veuillez réessayer.
              </Alert>
            ) : null}
            <Submit
              submitAlignment={SubmitAlignment.Right}
              successText="Envoyer mes retours"
              disabled={submitShortlist.isPending}
              onSuccessReset
            >
              Envoyer mes retours
            </Submit>
          </div>
        </div>
      </Form>
      <Confirm
        show={pendingValues !== null}
        title="Envoyer vos retours"
        content="Une fois envoyés, vos retours ne pourront plus être modifiés."
        textConfirm="Envoyer"
        onCancel={() => setPendingValues(null)}
        onConfirm={confirmSubmission}
      />
    </>
  )
}
